import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Button } from "reactstrap";
import {
  holdCart,
  clearCart,
  applyTax,
  toggleCustomerTCoins,
  retrieveCart,
  revokeDiscount,
} from "../../../ReduxToolkit/Reducers/CartReducer";
import {
  getFormattedPrice,
  getTaxRate,
  getVendorSettings,
  getGlobalSettings,
  roundToNearestNickel,
} from "../../../services/helper";
import { toast } from "react-toastify";
import DiscountHandler from "./Discount";
import EventDiscountButton from "./EventDiscount";
import shortcutManager from "../../../services/shortcutManager";

export const CartFooter = ({ onCheckout }) => {
  const dispatch = useDispatch();
  const {
    applyTax: applyTaxState,
    tax,
    cartItems,
    total,
    discount,
    applied_coupon,
    applied_event,
    redeemPoints,
    redeemedPointsValue,
    redeemTCoins,
    redeemedTCoinsValue,
  } = useSelector((state) => state.cart);

  const taxInfo = getTaxRate();

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0,
  );

  const allowGovtRounding = getVendorSettings("allow_govt_discount") === true;
  // console.log(allowGovtRounding);

  const displayTotal = parseFloat(total);

  const roundingAdjustment = 0;

  const isCartEmpty = cartItems.length === 0;

  React.useEffect(() => {
    const tax_info = applyTaxState ? taxInfo : { rate: 0, label: "" };
    dispatch(applyTax(tax_info));
  }, [applyTaxState, dispatch, taxInfo]);

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  const handleHoldCart = () => {
    dispatch(holdCart());
  };

  useEffect(() => {
    shortcutManager.unregisterContext("cart");

    if (!isCartEmpty) {
      shortcutManager.register("Alt+c", () => onCheckout(), "cart");
      shortcutManager.register("Alt+h", () => handleHoldCart(), "cart");
      shortcutManager.register("Escape", () => handleClearCart(), "cart");
    }

    shortcutManager.register("Alt+n", () => handlePrintNoSale(), "cart");

    for (let i = 1; i <= 9; i++) {
      shortcutManager.register(
        `Alt+${i}`,
        () => {
          const cartIndex = i - 1;
          const { holdCarts } = JSON.parse(
            localStorage.getItem("cart") || '{"holdCarts":[]}',
          );
          if (holdCarts && holdCarts.length > cartIndex) {
            dispatch(retrieveCart(holdCarts[cartIndex].id));
          }
        },
        "cart",
      );
    }

    return () => {
      shortcutManager.unregisterContext("cart");
    };
  }, [dispatch, isCartEmpty, onCheckout]);

  const handlePrintNoSale = () => {
    const name = getVendorSettings("name") || "N/A";
    const phone = getVendorSettings("vendor_contact_phone") || "N/A";
    const address = getVendorSettings("vendor_contact_address") || "N/A";

    const printContent = `
        <div style="text-align: center; font-family: Arial, sans-serif; padding-top:15px">
          <h2>${name}</h2>
          <p><strong>Phone:</strong> ${phone}</p>
          <p><strong>Address:</strong> ${address}</p>
        </div>
      `;

    const printWindow = window.open("", "", "width=500,height=400");

    if (printWindow) {
      printWindow.document.write(`
          <html>
            <head><title>Print Vendor Info</title></head>
            <body>${printContent}</body>
          </html>
        `);
      printWindow.document.close();
      printWindow.print();
    } else {
      console.error("Failed to open print window.");
    }
  };

  return (
    <div className="cart-total-section">
      <div className="cart-totals">
        <div className="cart-total-item" style={{ padding: "2px 0" }}>
          <span className="cart-total-label txt-primary">Subtotal:</span>
          <span className="cart-total-value">
            {getFormattedPrice(subtotal)}
          </span>
        </div>
        {(applyTaxState || (!applyTaxState && applied_event)) && (
          <div className="cart-total-item" style={{ padding: "2px 0" }}>
            <span className="cart-total-label txt-primary">Tax:</span>
            <span className="cart-total-value">
              {!applyTaxState && applied_event
                ? "No Tax"
                : getFormattedPrice(tax)}
            </span>
          </div>
        )}

        {!redeemPoints &&
          !redeemTCoins &&
          (discount > 0 ? (
            <div className="cart-total-item" style={{ padding: "2px 0" }}>
              <span className="cart-total-label txt-primary">Discount:</span>
              <div className="d-flex align-items-center gap-2">
                <span className="cart-total-value text-success">
                  -{getFormattedPrice(discount)}
                </span>
                <Button
                  onClick={() => dispatch(revokeDiscount())}
                  size="sm"
                  color="danger"
                  title="Remove discount"
                >
                  ×
                </Button>
              </div>
            </div>
          ) : (
            <DiscountHandler />
          ))}

        {!redeemPoints && !redeemTCoins && (
          <div className="cart-total-item" style={{ padding: "2px 0" }}>
            <EventDiscountButton />
          </div>
        )}

        {redeemPoints && (
          <div className="cart-total-item" style={{ padding: "2px 0" }}>
            <span className="cart-total-label txt-info">Redeem Points:</span>
            <span className="cart-total-value">
              {getFormattedPrice(redeemedPointsValue)}
            </span>
          </div>
        )}
        {redeemTCoins && (
          <div className="cart-total-item" style={{ padding: "2px 0" }}>
            <span className="cart-total-label txt-info">Redeem T-Coins:</span>
            <span className="cart-total-value">
              {getFormattedPrice(redeemedTCoinsValue)}
            </span>
          </div>
        )}
        {roundingAdjustment > 0 && (
          <div className="cart-total-item" style={{ padding: "2px 0" }}>
            <span className="cart-total-label txt-info">Govt Rounding:</span>
            <span className="cart-total-value">
              +{getFormattedPrice(roundingAdjustment)}
            </span>
          </div>
        )}
        <div className="cart-total-item" style={{ padding: "2px 0" }}>
          <span className="cart-total-label">Total:</span>
          <span className="cart-total-value">
            {getFormattedPrice(displayTotal)}
          </span>
        </div>
        {redeemedTCoinsValue > 0 && (
          <div className="d-flex justify-content-between">
            <Button
              color="info"
              className="btn-square w-100 mx-1"
              onClick={() => dispatch(toggleCustomerTCoins())}
            >
              {`Redeem ${redeemedTCoinsValue}T`}
            </Button>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center px-2 py-2">
          <Button
            disabled={isCartEmpty}
            color="success"
            className="w-100"
            onClick={onCheckout}
            style={{ height: "40px" }}
          >
            Checkout <small>(Alt+C)</small>
          </Button>
        </div>

        <div className="d-flex justify-content-between">
          <div className="d-flex flex-column" style={{ width: "49%" }}>
            <Button
              disabled={isCartEmpty}
              color="primary"
              className="mb-2"
              onClick={handleHoldCart}
              title="Alt+H"
              style={{ fontSize: 13, height: "32px", width: "100%" }}
            >
              Hold <small>(Alt+H)</small>
            </Button>

            <Button
              color="info"
              onClick={handlePrintNoSale}
              title="Alt+N"
              style={{ fontSize: 13, height: "32px", width: "100%" }}
            >
              No Sale <small>(Alt+N)</small>
            </Button>
          </div>

          <div className="d-flex flex-column" style={{ width: "49%" }}>
            <Button
              disabled={isCartEmpty}
              color="danger"
              onClick={handleClearCart}
              title="Esc"
              style={{ fontSize: 13, height: "32px", width: "100%" }}
            >
              Clear <small>(Esc)</small>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartFooter;
